// shared/src/oracle/feeds-config-store.ts
// D1 access for `oracle_feeds_config` (migration 0113) — the RSS feed lists as EDITABLE config,
// replacing the ten RSS_*_FEEDS / RSS_*_MAX runtime_params as the live source of truth (#120
// feed-CRUD). refresh-fetcher reads the enabled rows each fire and groups them by feed_group; the
// api-worker admin route upserts/deletes rows (add/edit/retire a feed from the /oracle News tab).
//
// Row/Zod/reader/writer live together and ship with the DDL in ONE commit (feedback_writer_ddl_lockstep).
// Mirrors relations-config-store.ts (#93): the free-form `feed_group` bucket means a NEW group needs no
// deploy. Uses the shared OracleD1 statement surface so the admin route can batch the config-history
// rows (oracleConfigHistoryInsertStmts) atomically with the upsert/delete.

import { z } from 'zod';
import type { OracleD1, OracleD1Stmt } from './oracle-store.js';

export interface OracleFeedConfigRow {
  readonly id:         number;         // synthetic row id (rowid alias; PATCH/DELETE key on it)
  readonly feed_group: string;         // bucket ('crypto' | 'cb' | 'rates' | 'news' | 'weather' | …)
  readonly url:        string;
  readonly max_items:  number;         // per-feed item cap contributed to the group digest
  readonly enabled:    number;         // 0/1 — SQLite has no bool; 0 = disabled (row kept), 1 = live
  readonly tier:       'A' | 'B' | 'C'; // #94: per-feed rank tier (migration 0116)
  readonly weight:     number;         // #94: per-feed rank weight (migration 0116)
  readonly note:       string | null;
  readonly updated_at: string;         // ISO-Z
  readonly updated_by: string;
}

export const OracleFeedConfigRowSchema = z.object({
  id:         z.number().int(),
  feed_group: z.string().min(1),
  url:        z.string().url(),
  max_items:  z.number().int().positive(),
  enabled:    z.number().int().min(0).max(1),
  tier:       z.enum(['A', 'B', 'C']),
  weight:     z.number(),
  note:       z.string().nullable(),
  updated_at: z.string().min(1),
  updated_by: z.string().min(1),
});

const COLS = 'id, feed_group, url, max_items, enabled, tier, weight, note, updated_at, updated_by';

/** ALL feed config rows (enabled + disabled), grouped then by id, for the dashboard CRUD table. */
export async function readFeedsConfig(db: OracleD1): Promise<OracleFeedConfigRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_feeds_config ORDER BY feed_group ASC, id ASC`)
    .all<OracleFeedConfigRow>();
  return res.results ?? [];
}

/** The CURRENTLY-LIVE feeds — enabled rows only, group-ordered. This is what refresh-fetcher's
 *  fetchRssAllGroups loops each fire; a disabled/deleted feed drops out and never reaches the LLM. */
export async function readEnabledFeeds(db: OracleD1): Promise<OracleFeedConfigRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM oracle_feeds_config WHERE enabled = 1 ORDER BY feed_group ASC, id ASC`)
    .all<OracleFeedConfigRow>();
  return res.results ?? [];
}

/** One feed config row by its synthetic id (PATCH/DELETE lookup). null when absent. */
export async function readFeedConfigById(db: OracleD1, id: number): Promise<OracleFeedConfigRow | null> {
  return db
    .prepare(`SELECT ${COLS} FROM oracle_feeds_config WHERE id = ?`)
    .bind(id)
    .first<OracleFeedConfigRow>();
}

/** INSERT a new feed config row; returns its DB-assigned id via `INSERT … RETURNING id`. Unlike the
 *  relation upsert, the id is server-assigned, so the audit-history row (keyed on that id) cannot be
 *  built until AFTER the insert — the admin route inserts here first, then writes the creation history
 *  in a follow-up call. The UNIQUE(feed_group, url) constraint surfaces a dup as a D1 error the route
 *  pre-checks into a 409. */
export async function insertFeedConfig(
  db: OracleD1,
  row: Omit<OracleFeedConfigRow, 'id'>,
): Promise<number> {
  const res = await db
    .prepare(
      `INSERT INTO oracle_feeds_config (feed_group, url, max_items, enabled, tier, weight, note, updated_at, updated_by)
       VALUES (?,?,?,?,?,?,?,?,?)
       RETURNING id`,
    )
    .bind(row.feed_group, row.url, row.max_items, row.enabled, row.tier, row.weight, row.note, row.updated_at, row.updated_by)
    .first<{ id: number }>();
  if (res?.id == null) throw new Error('insertFeedConfig: INSERT ... RETURNING id yielded no id');
  return res.id;
}

/** UPDATE an existing feed config row (identified by id) with the full merged row. Extra statements
 *  (config-history) batch atomically with the write (mirrors upsertRelationConfig). */
export async function updateFeedConfig(
  db: OracleD1,
  row: OracleFeedConfigRow,
  extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const update = db
    .prepare(
      `UPDATE oracle_feeds_config SET
         feed_group=?, url=?, max_items=?, enabled=?, tier=?, weight=?, note=?, updated_at=?, updated_by=?
       WHERE id=?`,
    )
    .bind(
      row.feed_group, row.url, row.max_items, row.enabled, row.tier, row.weight, row.note,
      row.updated_at, row.updated_by, row.id,
    );
  await db.batch([update, ...extraStmts]);
}

/** Hard-delete a feed config row by id (with optional batched history statements). Disabling a feed
 *  usually sets enabled=0 to keep its edit history readable; delete is for a mistaken add. */
export async function deleteFeedConfig(
  db: OracleD1, id: number, extraStmts: OracleD1Stmt[] = [],
): Promise<void> {
  const del = db.prepare('DELETE FROM oracle_feeds_config WHERE id = ?').bind(id);
  await db.batch([del, ...extraStmts]);
}
